import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { trackEvent } from './api';
import { EVENTS } from './events';

function uniq(arr) {
  const out = [];
  const seen = new Set();
  for (const v of arr) {
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

function parseDriveIdFromUrl(url) {
  if (typeof url !== 'string') return null;

  // https://lh3.googleusercontent.com/d/<ID>=w800
  const m1 = url.match(/^https?:\/\/lh3\.googleusercontent\.com\/d\/([^=/?#]+)=/);
  if (m1) return m1[1];

  // https://drive.google.com/thumbnail?id=<ID>&sz=w800
  const m2 = url.match(/[?&]id=([^&]+)/);
  if (m2) return decodeURIComponent(m2[1]);

  // https://drive.google.com/file/d/<ID>/view
  const m3 = url.match(/\/file\/d\/([^/]+)/);
  if (m3) return m3[1];

  return null;
}

function getFallbackCandidates(originalSrc) {
  if (!originalSrc) return [];
  const id = parseDriveIdFromUrl(originalSrc);

  // If we can derive an ID, try a couple of alternative endpoints.
  // Goal: survive occasional blocking/quota differences across endpoints.
  if (id) {
    return uniq([
      originalSrc,
      `https://lh3.googleusercontent.com/d/${id}=w800`,
      `https://lh3.googleusercontent.com/d/${id}=w1600`,
      `https://drive.google.com/thumbnail?id=${encodeURIComponent(id)}&sz=w800`,
      `https://drive.google.com/thumbnail?id=${encodeURIComponent(id)}&sz=w1600`,
      `https://drive.google.com/uc?export=view&id=${encodeURIComponent(id)}`,
    ]);
  }

  return [originalSrc];
}

/**
 * A drop-in <img> that retries a few equivalent URLs on load failure.
 * This helps when clients/extensions/network intermittently block one endpoint.
 */
export function ResilientImg({
  src,
  alt = '',
  onError,
  candidates,
  track = true,
  loading,
  decoding,
  referrerPolicy,
  rotateDeg,
  ...imgProps
}) {
  const srcCandidates = useMemo(() => {
    const list = typeof candidates === 'function' ? candidates(src) : getFallbackCandidates(src);
    return Array.isArray(list) && list.length > 0 ? list : [src];
  }, [candidates, src]);

  const [idx, setIdx] = useState(0);
  const activeSrc = srcCandidates[Math.min(idx, srcCandidates.length - 1)] || src;

  useEffect(() => {
    setIdx(0);
  }, [src]);

  const handleError = useCallback(
    (e) => {
      const nextIdx = idx + 1;
      if (nextIdx < srcCandidates.length) {
        setIdx(nextIdx);
        return;
      }

      if (track) {
        try {
          trackEvent(EVENTS.PHOTO_LOAD_ERROR, { src, active_src: activeSrc });
        } catch (_) {}
      }

      if (typeof onError === 'function') onError(e);
    },
    [activeSrc, idx, onError, src, srcCandidates.length, track]
  );

  const finalStyle = useMemo(() => {
    const baseStyle = imgProps.style || {};
    const withOrientation = { imageOrientation: 'from-image', ...baseStyle };
    if (rotateDeg == null || Number.isNaN(rotateDeg)) {
      return withOrientation;
    }
    const baseTransform = baseStyle.transform || '';
    const rotateStr = `rotate(${rotateDeg}deg)`;
    const nextTransform = baseTransform
      ? `${baseTransform} ${rotateStr}`
      : rotateStr;
    return { ...withOrientation, transform: nextTransform };
  }, [imgProps.style, rotateDeg]);

  return (
    <img
      {...imgProps}
      style={finalStyle}
      src={activeSrc}
      alt={alt}
      loading={loading || 'lazy'}
      decoding={decoding || 'async'}
      referrerPolicy={referrerPolicy || 'no-referrer'}
      onError={handleError}
    />
  );
}

